/**
 * Property Area Unit Conversion Utilities for ZenStudios
 * ======================================================
 * Client-side area conversion with real-time preview support.
 */

// Conversion constants (to square feet)
export const CONVERSION_TO_SQFT = {
  sqft: 1,
  sqyd: 9,           // 1 sq yard = 9 sq ft
  acres: 43560,      // 1 acre = 43,560 sq ft
  guntas: 1089,      // 1 gunta = 1,089 sq ft
  cents: 435.6,      // 1 cent = 435.6 sq ft
};

// Unit display names
export const UNIT_DISPLAY_NAMES = {
  sqft: 'Sq Ft',
  sqyd: 'Sq Yards',
  acres: 'Acres',
  guntas: 'Guntas',
  cents: 'Cents',
};

// Unit options for dropdown
export const AREA_UNIT_OPTIONS = [
  { value: 'sqft', label: 'Sq Ft' },
  { value: 'sqyd', label: 'Sq Yards' },
  { value: 'acres', label: 'Acres' },
  { value: 'guntas', label: 'Guntas' },
  { value: 'cents', label: 'Cents' },
];

// Default units by property type/subtype
export const DEFAULT_UNITS_BY_TYPE = {
  // Residential
  residential: 'sqft',
  apartment: 'sqft',
  flat: 'sqft',
  villa: 'sqft',
  'independent house': 'sqft',
  penthouse: 'sqft',
  studio: 'sqft',
  
  // Commercial
  office: 'sqft',
  shop: 'sqft',
  showroom: 'sqft',
  commercial: 'sqft',
  warehouse: 'sqft',
  industrial: 'sqft',
  co_working: 'sqft',
  
  // Plots
  'residential plot': 'sqyd',
  plot: 'sqyd',
  
  // Agricultural/Rural - Main type defaults to acres
  agricultural: 'acres',
  'agricultural land': 'acres',
  'agricultural_plot': 'acres',
  'farm land': 'acres',
  farmland: 'acres',
  'farm house': 'acres',
  farmhouse: 'acres',
  
  // Village/Rural
  'village land': 'guntas',
  'rural land': 'guntas',
};

/**
 * Convert area from any unit to square feet
 * @param {number} value - Area value
 * @param {string} fromUnit - Source unit
 * @returns {number} Area in square feet
 */
export const toSqft = (value, fromUnit) => {
  if (!value || value <= 0) return 0;
  const factor = CONVERSION_TO_SQFT[fromUnit] || 1;
  return Math.round(value * factor * 100) / 100;
};

/**
 * Convert area from square feet to any unit
 * @param {number} sqftValue - Area in square feet
 * @param {string} toUnit - Target unit
 * @returns {number} Converted area
 */
export const fromSqft = (sqftValue, toUnit) => {
  if (!sqftValue || sqftValue <= 0) return 0;
  const factor = CONVERSION_TO_SQFT[toUnit] || 1;
  return Math.round((sqftValue / factor) * 100) / 100;
};

/**
 * Get all unit conversions from square feet
 * @param {number} sqftValue - Area in square feet
 * @returns {Object} All conversions
 */
export const getAllConversions = (sqftValue) => {
  if (!sqftValue || sqftValue <= 0) {
    return Object.keys(CONVERSION_TO_SQFT).reduce((acc, unit) => {
      acc[unit] = 0;
      return acc;
    }, {});
  }
  
  return Object.keys(CONVERSION_TO_SQFT).reduce((acc, unit) => {
    acc[unit] = fromSqft(sqftValue, unit);
    return acc;
  }, {});
};

/**
 * Get default unit for a property type
 * @param {string} propertyType - Property type or subtype
 * @returns {string} Default unit code
 */
export const getDefaultUnit = (propertyType) => {
  if (!propertyType) return 'sqft';
  const normalized = propertyType.toLowerCase().trim();
  return DEFAULT_UNITS_BY_TYPE[normalized] || 'sqft';
};

/**
 * Format area value with unit for display
 * @param {number} value - Area value
 * @param {string} unit - Unit code
 * @returns {string} Formatted string
 */
export const formatArea = (value, unit) => {
  if (!value || value <= 0) return '0 ' + UNIT_DISPLAY_NAMES[unit || 'sqft'];
  
  const unitLabel = UNIT_DISPLAY_NAMES[unit] || unit;
  const formatted = value >= 1000 
    ? value.toLocaleString('en-IN', { maximumFractionDigits: 2 })
    : value.toFixed(2).replace(/\.?0+$/, '');
  
  return `${formatted} ${unitLabel}`;
};

/**
 * Validate area input
 * @param {number} value - Area value
 * @param {string} unit - Unit code
 * @returns {Object} { isValid, error, sqftValue }
 */
export const validateAreaInput = (value, unit) => {
  if (!unit || !CONVERSION_TO_SQFT[unit]) {
    return { 
      isValid: false, 
      error: 'Invalid unit selected',
      sqftValue: null 
    };
  }
  
  if (value === undefined || value === null || value === '') {
    return { 
      isValid: false, 
      error: 'Area is required',
      sqftValue: null 
    };
  }
  
  const numValue = parseFloat(value);
  
  if (isNaN(numValue)) {
    return { 
      isValid: false, 
      error: 'Please enter a valid number',
      sqftValue: null 
    };
  }
  
  if (numValue < 0) {
    return { 
      isValid: false, 
      error: 'Area cannot be negative',
      sqftValue: null 
    };
  }
  
  if (numValue === 0) {
    return { 
      isValid: false, 
      error: 'Area cannot be zero',
      sqftValue: null 
    };
  }
  
  const sqftValue = toSqft(numValue, unit);
  
  // Min 10 sqft
  if (sqftValue < 10) {
    return { 
      isValid: false, 
      error: 'Area is too small (minimum 10 sq ft)',
      sqftValue: null 
    };
  }
  
  // Max 10,000 acres
  if (sqftValue > 435600000) {
    return { 
      isValid: false, 
      error: 'Area exceeds maximum allowed limit',
      sqftValue: null 
    };
  }
  
  return { 
    isValid: true, 
    error: null, 
    sqftValue 
  };
};

/**
 * Get conversion preview items (excluding the current unit)
 * @param {number} sqftValue - Area in sqft
 * @param {string} currentUnit - Current selected unit
 * @param {number} maxItems - Max items to show
 * @returns {Array} Preview items
 */
export const getConversionPreview = (sqftValue, currentUnit, maxItems = 4) => {
  if (!sqftValue || sqftValue <= 0) return [];
  
  const conversions = getAllConversions(sqftValue);
  
  // Priority order for display
  const priorityOrder = ['sqft', 'sqyd', 'guntas', 'acres', 'cents'];
  
  return priorityOrder
    .filter(unit => unit !== currentUnit)
    .slice(0, maxItems)
    .map(unit => ({
      unit,
      value: conversions[unit],
      label: UNIT_DISPLAY_NAMES[unit],
      formatted: formatArea(conversions[unit], unit)
    }));
};

export default {
  CONVERSION_TO_SQFT,
  UNIT_DISPLAY_NAMES,
  AREA_UNIT_OPTIONS,
  toSqft,
  fromSqft,
  getAllConversions,
  getDefaultUnit,
  formatArea,
  validateAreaInput,
  getConversionPreview,
};
